import { Box, Container } from '@mantine/core';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import { resolveIntakeOrganizationIdFromPatient } from '@/lib/resolveIntakeOrganizationFromPatient';
import { IntakeFlow } from '../public/intake/IntakeFlow';
import type { IntakeFlowVariant } from '../public/intake/intakeFlowVariant';
import type { TherapyType } from '../public/intake/types';
import '../public/intake/intake.css';

export interface ClientIntakePageProps {
  /** Client portal: pass active patient id so Stripe SetupIntent attaches to the same subject as billing. */
  billingPatientId?: string;
  flowVariant?: IntakeFlowVariant;
  excludedTherapyTypes?: TherapyType[];
  /** When parent already resolved org id (e.g. request-service page). */
  intakeOrganizationId?: string;
  onComplete?: () => void;
  completionCloseLabel?: string;
}

/**
 * Client Intake page: intake flow presented inline (no modal).
 * Same steps and behavior as the modal flow, rendered on the page.
 */
export function ClientIntakePage({
  billingPatientId,
  flowVariant = 'full',
  excludedTherapyTypes,
  intakeOrganizationId: intakeOrganizationIdProp,
  onComplete,
  completionCloseLabel,
}: ClientIntakePageProps): JSX.Element {
  const medplum = useMedplum();
  const [intakeOrganizationId, setIntakeOrganizationId] = useState<string | undefined>(
    intakeOrganizationIdProp,
  );

  useEffect(() => {
    setIntakeOrganizationId(intakeOrganizationIdProp);
  }, [intakeOrganizationIdProp]);

  useEffect(() => {
    if (!billingPatientId || intakeOrganizationIdProp) {
      if (!intakeOrganizationIdProp) setIntakeOrganizationId(undefined);
      return;
    }
    let cancelled = false;
    void resolveIntakeOrganizationIdFromPatient(medplum, billingPatientId)
      .then((orgId) => {
        if (!cancelled) setIntakeOrganizationId(orgId);
      })
      .catch(() => {
        if (!cancelled) setIntakeOrganizationId(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, billingPatientId, intakeOrganizationIdProp]);

  return (
    <Box py="xl" style={{ minHeight: '60vh' }}>
      <Container size="lg">
        <IntakeFlow
          showCloseButton={false}
          billingPatientId={billingPatientId}
          intakeOrganizationId={intakeOrganizationId}
          flowVariant={flowVariant}
          excludedTherapyTypes={excludedTherapyTypes}
          onComplete={onComplete}
          completionCloseLabel={completionCloseLabel}
        />
      </Container>
    </Box>
  );
}
